<!-- eslint-disable vue/no-v-html -->
<template>
  <div class="registration-form__basic-info-container">
    <div v-if="isShowEmailDisclaimer">
      <UtdNotification id="emailDisclaimer"
                       type="info"
                       notification-mode="info"
                       :has-close-notification="true"
                       @closed="isShowEmailDisclaimer = false">
        <template #title>
          {{ $t(C_ACCOUNT.FORM_MESSAGING.EMAIL_NOTIFICATION_TITLE_REDESIGN) }}
        </template>
        <template #listContent>
          <ul class="registration-form__email-notification-list">
            <li v-for="(message, index) in formattedEmailNotification" 
                :key="index"
                class="registration-form__email-notification-list-item">
              <span v-html="message" />
            </li>
          </ul>
        </template>
      </UtdNotification>
    </div>
    <fieldset v-show="formFields.salutation && !formFields.salutation.isHidden"
              id="salutationField"
              class="wk-js2 registration-form-field">
      <UtdSelect id="salutationSelect"
                 :selected="form.salutation"
                 :options="SALUTATION_OPTIONS"
                 label="Title"
                 :required="formFields.salutation?.required"
                 placeholder="Select a title"
                 @input="emit('selectChange', { value: $event, name: REG_FIELD.SALUTATION })"
      />
    </fieldset>
    <fieldset v-show="formFields.firstName && !formFields.firstName.isHidden" 
              id="firstNameField"
              class="wk-js2 registration-form-field">
      <UtdTextInput label="First Name"
                    :is-error="checkFieldInvalid(REG_FIELD.FIRST_NAME) && isDirty.firstName"
                    :is-disabled="!isEditableName(formFields.firstName)"
                    :error-message="$t(getFieldErrorMessage(REG_FIELD.FIRST_NAME))"
                    :required="formFields.firstName?.required">
        <input id="registrationFirstName"
               v-model="form.firstName"
               aria-label="First Name"
               type="text"
               placeholder="Enter a first name"
               :disabled="!isEditableName(formFields.firstName)"
               :maxlength="formFields.firstName?.maxLength"
               class="wk-field-input form-field jumpstart-input"
               autocomplete="given-name"
               @input="checkResetServerError(REG_FIELD.FIRST_NAME)"
               @blur="() => isDirty.firstName = true">
      </UtdTextInput>
    </fieldset>
    
    <fieldset v-show="formFields.lastName && !formFields.lastName.isHidden" 
              id="lastNameField"
              class="wk-js2 registration-form-field">
      <UtdTextInput label="Last Name"
                    :is-error="checkFieldInvalid(REG_FIELD.LAST_NAME) && isDirty.lastName"
                    :is-disabled="!isEditableName(formFields.lastName)"
                    :error-message="$t(getFieldErrorMessage(REG_FIELD.LAST_NAME))"
                    :required="formFields.lastName?.required">
        <input id="lastName"
               v-model="form.lastName"
               type="text"
               placeholder="Enter a last name"
               :disabled="!isEditableName(formFields.lastName)"
               aria-label="Last Name"
               :maxlength="formFields.lastName?.maxLength"
               class="wk-field-input form-field jumpstart-input"
               autocomplete="family-name"
               @input="checkResetServerError(REG_FIELD.LAST_NAME)"
               @blur="() => isDirty.lastName = true">
      </UtdTextInput>
    </fieldset>

    <fieldset v-show="formFields.suffix && !formFields.suffix.isHidden"
              id="suffixField"
              class="wk-js2 registration-form-field">
      <UtdTextInput label="Suffix"
                    :optional="true"
                    :is-error="false"
                    :is-disabled="!formFields.suffix?.isEditable"
                    :required="formFields.suffix?.required">
        <input id="suffix"
               v-model="form.suffix"
               type="text"
               placeholder="Enter a suffix (optional)"
               :disabled="!formFields.suffix?.isEditable"
               aria-label="Suffix"
               :maxlength="formFields.suffix?.maxLength"
               class="wk-field-input form-field jumpstart-input"
               autocomplete="honorific-suffix"
               @input="checkResetServerError('suffix')"
               @blur="() => isDirty.suffix = true">
      </UtdTextInput>
    </fieldset>
    
    <fieldset v-show="formFields.email && !formFields.email.isHidden" 
              id="emailField"
              class="wk-js2 registration-form-field">
      <UtdTextInput label="Contact Email"
                    :is-error="shouldShowEmailError"
                    :is-disabled="!formFields.email?.isEditable"
                    :error-message="$t(getFieldErrorMessage(REG_FIELD.EMAIL))"
                    :required="formFields.email?.required">
        <input id="email"
               v-model="form.email"
               type="email"
               placeholder="Enter a contact email"
               :disabled="!formFields.email?.isEditable"
               aria-label="Contact email"
               :maxlength="formFields.email?.maxLength"
               class="wk-field-input form-field jumpstart-input"
               autocomplete="email"
                 @input="checkResetServerError(REG_FIELD.EMAIL)"
               @blur="handleEmailBlur">
      </UtdTextInput>
    </fieldset>

    <MergeSuggestionNotification v-if="showMergeSuggestion"
                                 :is-ehr-user="isEhrUser"
                                 @closed="handleCloseMergeSuggestion"
                                 @merge-clicked="handleMergeClicked" />

    <fieldset v-show="formFields.phone1 && !formFields.phone1.isHidden" 
              id="phone1Field"
              class="wk-js2 registration-form-field">
      <UtdPhoneInput ref="phoneInputRef"
                     label="Phone Number"
                     placeholder="Enter a phone number"
                     :options="phoneOptions"
                     :required="formFields.phone1?.required"
                     :input="phoneInput"
                     :is-dirty-override="isDirty.phone1"
                     @on-phone-ext-input="handlePhoneExtInput"
                     @on-phone-type-change="handlePhoneType"
                     @on-phone-input-blur="handlePhoneBlur"
                     @on-phone-input="handlePhoneInput" />
    </fieldset>
  </div>
</template>

<script setup>
import UtdTextInput from '_acaSrc/components/shared/input/UtdTextInput.vue';
import UtdPhoneInput from '_acaSrc/components/shared/input/UtdPhoneInput.vue';
import UtdSelect from '_acaSrc/components/shared/input/UtdSelect.vue';
import { 
    C_ACCOUNT,
    UTD_WAYF_EMAIL_CHANGED_NOTIFICATION,
    isOidcRegex,
    SALUTATION_OPTIONS,
    REG_FIELD
} from '_acaSrc/utility/constants';
import UtdNotification from '_acaSrc/components/shared/utd/UtdNotification.vue';
import MergeSuggestionNotification from
    '_acaSrc/components/register/MergeSuggestionNotification.vue';
import utdRest from '_acaSrc/utility/http/UtdRestHooks';
import useRegistrationForm from '_acaSrc/composables/registrationForm.composable';
import useEmailCheck from '_acaSrc/composables/useEmailCheck.composable';
import { SET_SHOULD_SUGGEST_MERGE } from '_acaSrc/store/login.store.js';
import { useStore } from 'vuex';
import { ref, computed } from 'vue';
const store = useStore();

const registrationForm = computed(() => store.getters['login/registrationForm']);
const formFields = computed(() => registrationForm.value.formFields);
const shouldSuggestMerge = computed(() => registrationForm.value.shouldSuggestMerge);

const supportTag = computed(() => store.getters['footer/supportTag']);
const isSso = computed(() => isOidcRegex.test(supportTag.value.serverTag));

const emit = defineEmits([
    'selectChange',
    'close',
    'merge-clicked'
]);

const shouldShowEmailError = computed(() => {
    return (checkFieldInvalid(REG_FIELD.EMAIL) && isDirty.email) || needsNewCode.value;
});

const phoneInput = computed(() => {
    return {
        inputSize: 'small',
        phoneValue: form.phone1,
        phoneType: form.phone1Type
    };
});

const phoneOptions = computed(() => {
    return {
        showError: true,
        showSuccess: false,
        isPhoneRequired: formFields.value.phone1?.required,
        showPhoneExt: true,
        serverError: serverError.phone1,
        isDisabled: !formFields.value.phone1?.isEditable
    };
});

const handlePhoneBlur = () => {
    isDirty.phone1 = true;
};

const handlePhoneInput = (phone) => {
    form.phone1 = phone;
    checkResetServerError(REG_FIELD.PHONE1);
};

const handlePhoneType = (phoneType) => {
    form.phone1Type = phoneType;
};

const handlePhoneExtInput = (phoneExt) => {
    form.phone1Ext = phoneExt;
};

const {
    form,
    isDirty,
    serverError,
    needsNewCode,
    checkResetServerError,
    checkFieldInvalid,
    getFieldErrorMessage,
    isHasPreFilledEmail
} = useRegistrationForm();

const {
    checkEmailAvailability,
    resetEmailAvailability
} = useEmailCheck();

const disclaimerEmail = ref('');
const isShowEmailDisclaimer = ref(false);

const isEhrUser = computed(() => store.getters['profile/userProfileVisitor'].isEMRAuthenticated);
const isLoggedIn = computed(() => store.getters['profile/userProfileVisitor'].isLoggedIn);
const isRegistered = computed(() => store.getters['profile/userProfileVisitor'].isRegistered);
const isOidcUser = computed(() => store.getters['auth/isOidcUser']);

const showMergeSuggestion = computed(() => {
    return isLoggedIn.value 
        && !isRegistered.value 
        && (isEhrUser.value || isOidcUser.value == '')
        && shouldSuggestMerge.value;
});

const getContactEmailDomain = computed(() => form.email.split('@')[1] || '');
const isShouldSkipDomainVerification = computed(() =>
    !isSso.value
    || !isHasPreFilledEmail.value
    || checkFieldInvalid(REG_FIELD.EMAIL));

const checkEmailDuplicate = async() => {
    if (!form.email || checkFieldInvalid(REG_FIELD.EMAIL)) {
        store.commit(`login/${SET_SHOULD_SUGGEST_MERGE}`, false);
        resetEmailAvailability();
        return;
    }

    try {
        const isAvailable = await checkEmailAvailability(form.email);
        store.commit(`login/${SET_SHOULD_SUGGEST_MERGE}`, isAvailable === false);
    }
    catch {
        store.commit(`login/${SET_SHOULD_SUGGEST_MERGE}`, false);
    }
};

const handleEmailBlur = async() => {
    if (isLoggedIn.value) {
        await checkEmailDuplicate();
    }
    await toggleEmailDisclaimerForSsoEmail();
};

const toggleEmailDisclaimerForSsoEmail = async() => {
    if (isShouldSkipDomainVerification.value) {
        isDirty.email = true;
        isShowEmailDisclaimer.value = false;
        return;
    }

    try {
        const data = await utdRest('wayf/institutions/disclaimer-email', {
            emailDomain: getContactEmailDomain.value
        });

        if (data?.data?.trim()) {
            disclaimerEmail.value = data.data;
            isShowEmailDisclaimer.value = true;
        }
        else {
            disclaimerEmail.value = '';
            isShowEmailDisclaimer.value = false;
        }
    }
    catch {
        // do nothing
    }
    finally {
        isDirty.email = true;
    }
};

const formattedEmailNotification = computed(() => {
    return UTD_WAYF_EMAIL_CHANGED_NOTIFICATION.map((msg) =>
        msg.replace('{disclaimerEmail}', disclaimerEmail.value)
    );
});

const FIELD_TYPES = {
    HIDDEN: 'HIDDEN',
    LABEL: 'LABELVALUE'
};
const isFullNameEditable = computed(() =>
    formFields.value.fullName?.type === FIELD_TYPES.LABEL && formFields.value.fullName.isEditable
);
const isEditableName = (name) => {
    const isHidden = name?.type === FIELD_TYPES.HIDDEN || name?.isHidden;

    return isHidden ? isFullNameEditable.value : name?.isEditable;
};

const handleCloseMergeSuggestion = () => {
    store.commit(`login/${SET_SHOULD_SUGGEST_MERGE}`, false);
};

const handleMergeClicked = () => {
    emit('merge-clicked');
};
</script>

<style lang="less" scoped>
@import (reference) '~_acaAssets/wkce/colors/wkce-app-styles';

@media screen and (min-width: 768px) {
  .registration-form__basic-info-container {
    :deep(.wk-field.utd-phone-ext-input) {
      .ds1-mt-0();
    }

    :deep(#phone1Field .wk-field.wk-field-horizontal-choices) {
      .ds1-mt-0();
    }
  }
}

.registration-form__basic-info-container {
  :deep(.utd-phone-input) {
    .ds1-mb-0();
    display: flex;
    flex-wrap: wrap;
    max-width: 320px;
  }

  :deep(.utd-phone-text-input) {
    .ds1-mr-1();
    max-width: 220px;
  }

  :deep(.utd-phone-ext-input) {
    .ds1-mt-1();
    max-width: 75px;
  }

  :deep(.wk-field-input) {
    background-color: @DS1-UTD-PRIMARY-BG-COLOR;
  }

  :deep(.wk-field-label span) {
    color: @WKCE-GRAY-SHADE1;
  }

  :deep(.wk-field-header span) {
    color: @WKCE-GRAY-SHADE1;
  }

  :deep(#phone1Field .wk-field-horizontal-choices) {
    .ds1-mt-1();
  }

  :deep(#phone1Field .wk-field-horizontal-choices .wk-field-body) {
    align-items: center;
    flex-direction: row;
  }

  :deep(.wk-js2 .wk-field-horizontal-choices .wk-field-body > label) {
    display: flex;
  }
}

#emailDisclaimer {
  .ds1-mb-3();
}

.registration-form__email-notification-list {
  .ds1-mv-1();
  .ds1-pl-3();
  width: 85%;
  color: @DS1-UTD-TOPIC-TEXT-COLOR;
}

.registration-form__email-notification-list-item {
  margin-bottom: 4px;
  line-height: 1.6;
}

.wk-field-is-disabled {
  opacity: 1;

  .wk-field-input.form-field.jumpstart-input {
    background-color: @WKCE-GRAY-TINT5;
  }
}

</style>